import type {
  CumulativeMetricSeries,
  Dimension,
  MetricSeries,
} from "../../../domain/models/metrics";
import { startOfDay } from "../../../shared/utils/date";
import {
  ActivityLineChartDataKey,
  formatDateByDimension,
  getXAxisTicks,
  roundToOneDecimalPlace,
} from "./chartDataUtils";

export type ActivityTrendMetricKey =
  | ActivityLineChartDataKey.HoursInVr
  | ActivityLineChartDataKey.UniqueActiveUsers
  | ActivityLineChartDataKey.UserSessions;

export const DEFAULT_ACTIVITY_TREND_METRIC: ActivityTrendMetricKey =
  ActivityLineChartDataKey.UserSessions;

export const ACTIVITY_TREND_METRIC_KEYS: ActivityTrendMetricKey[] = [
  DEFAULT_ACTIVITY_TREND_METRIC,
  ActivityLineChartDataKey.HoursInVr,
  ActivityLineChartDataKey.UniqueActiveUsers,
];

export interface ActivityTrendDataPoint {
  [ActivityLineChartDataKey.Date]: string;
  [ActivityLineChartDataKey.TickLabel]: string;
  [ActivityLineChartDataKey.PeriodStart]: Date;
  [ActivityLineChartDataKey.PeriodEnd]: Date;
  [ActivityLineChartDataKey.HoursInVr]: number | null;
  [ActivityLineChartDataKey.UserSessions]: number | null;
  [ActivityLineChartDataKey.UniqueActiveUsers]: number | null;
}

export type ActivityTrendSeries = Partial<
  Record<ActivityTrendMetricKey, MetricSeries>
>;

export const toActivityTrendSeries = (
  metrics: CumulativeMetricSeries,
): ActivityTrendSeries => ({
  [ActivityLineChartDataKey.UserSessions]: metrics.userSessions,
  [ActivityLineChartDataKey.HoursInVr]: metrics.hoursInVr,
  [ActivityLineChartDataKey.UniqueActiveUsers]: metrics.uniqueUsers,
});

const sumValuesByPeriod = (
  series: MetricSeries | undefined,
  dimension: Dimension,
): Map<string, number> => {
  const totals = new Map<string, number>();

  series?.points.forEach((point) => {
    const key = formatDateByDimension(point.periodStart, dimension);
    totals.set(key, (totals.get(key) ?? 0) + point.value);
  });

  return totals;
};

const readMetricValue = (
  totals: Map<string, number> | undefined,
  periodKey: string,
  metric: ActivityTrendMetricKey,
): number | null => {
  const total = totals?.get(periodKey);

  if (total === undefined) {
    return null;
  }

  return metric === ActivityLineChartDataKey.HoursInVr
    ? roundToOneDecimalPlace(total)
    : total;
};

export const buildActivityTrendChartData = (
  startDate: Date,
  endDate: Date,
  dimension: Dimension,
  series: ActivityTrendSeries,
): ActivityTrendDataPoint[] => {
  const totalsByMetric = new Map<ActivityTrendMetricKey, Map<string, number>>(
    ACTIVITY_TREND_METRIC_KEYS.map((metric) => [
      metric,
      sumValuesByPeriod(series[metric], dimension),
    ]),
  );

  const rangeStart = startOfDay(startDate);

  return getXAxisTicks(startDate, endDate, dimension).map(
    ({ key, label, periodEnd }) => ({
      [ActivityLineChartDataKey.Date]: key,
      [ActivityLineChartDataKey.TickLabel]: label,
      [ActivityLineChartDataKey.PeriodStart]: new Date(rangeStart),
      [ActivityLineChartDataKey.PeriodEnd]: periodEnd,
      [ActivityLineChartDataKey.UserSessions]: readMetricValue(
        totalsByMetric.get(ActivityLineChartDataKey.UserSessions),
        key,
        ActivityLineChartDataKey.UserSessions,
      ),
      [ActivityLineChartDataKey.HoursInVr]: readMetricValue(
        totalsByMetric.get(ActivityLineChartDataKey.HoursInVr),
        key,
        ActivityLineChartDataKey.HoursInVr,
      ),
      [ActivityLineChartDataKey.UniqueActiveUsers]: readMetricValue(
        totalsByMetric.get(ActivityLineChartDataKey.UniqueActiveUsers),
        key,
        ActivityLineChartDataKey.UniqueActiveUsers,
      ),
    }),
  );
};

export const carryForwardActivityTrendGaps = (
  data: ActivityTrendDataPoint[],
): ActivityTrendDataPoint[] => {
  const lastValueByMetric = new Map<ActivityTrendMetricKey, number>();

  return data.map((point) => {
    const filled = { ...point };

    ACTIVITY_TREND_METRIC_KEYS.forEach((metric) => {
      const value = point[metric];

      if (value !== null) {
        lastValueByMetric.set(metric, value);

        return;
      }

      const carried = lastValueByMetric.get(metric);

      if (carried !== undefined) {
        filled[metric] = carried;
      }
    });

    return filled;
  });
};

const isReportedLater = (
  data: ActivityTrendDataPoint[],
  metric: ActivityTrendMetricKey,
): boolean => data.slice(1).some((point) => point[metric] !== null);

const NO_ACTIVITY_VALUE = 0;

const withRangeStart = (
  data: ActivityTrendDataPoint[],
): ActivityTrendDataPoint | null => {
  const point = data[0];

  if (!point) return null;

  const anchorable = ACTIVITY_TREND_METRIC_KEYS.filter(
    (metric) => point[metric] === null && isReportedLater(data, metric),
  );

  if (anchorable.length === 0) return null;

  const extended = { ...point };

  anchorable.forEach((metric) => {
    extended[metric] = NO_ACTIVITY_VALUE;
  });

  return extended;
};

export const anchorActivityTrendRangeStart = (
  data: ActivityTrendDataPoint[],
): ActivityTrendDataPoint[] => {
  const first = withRangeStart(data);

  if (!first) return data;

  const anchored = [...data];
  anchored[0] = first;

  return anchored;
};

export const plotUnreportedMetricsAtRangeStart = (
  data: ActivityTrendDataPoint[],
): ActivityTrendDataPoint[] => {
  const unreported = ACTIVITY_TREND_METRIC_KEYS.filter((metric) =>
    data.every((point) => point[metric] === null),
  );

  if (data.length === 0 || unreported.length === 0) return data;

  return data.map((point, index) => {
    if (index !== 0) return point;

    const plotted = { ...point };

    unreported.forEach((metric) => {
      plotted[metric] = NO_ACTIVITY_VALUE;
    });

    return plotted;
  });
};

export const isActivityTrendDataEmpty = (
  data: ActivityTrendDataPoint[],
): boolean =>
  data.every((point) =>
    ACTIVITY_TREND_METRIC_KEYS.every((metric) => point[metric] === null),
  );
